import type { FC } from "react";
import { BriefcaseIcon, MapPinIcon, ShieldCheckIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { CompactLayout, CompactLayoutSkeleton } from "./compact-layout";
import { DotList } from "./dot-list";
import { ExperienceBlock } from "./experience-block";
import { FavoriteButton } from "./favorite-button";
import { IconText } from "./icon-text";
import type { CardLayoutProps } from "./layout-props";
import { PractitionerActions } from "./practitioner-actions";
import { PractitionerAvatar } from "./practitioner-avatar";
import { PractitionerTags } from "./practitioner-tags";
import { ProfessionList } from "./profession-list";
import { SkeletonBar } from "./skeleton-bar";
import { SpecialtyPill } from "./specialty-pill";
import { VerifiedName } from "./verified-name";

const DirectoryLayoutSkeleton: FC = () => (
  <>
    <div className="hidden items-center gap-16 p-24 lg:flex">
      <div className="skeleton-bar-dark h-178 w-162 shrink-0 rounded-medium" />
      <div className="flex min-w-0 flex-1 flex-col gap-8">
        <div className="flex gap-8">
          <SkeletonBar tone="dark" className="h-37 w-244" />
          <SkeletonBar className="h-37 w-176" />
          <SkeletonBar className="h-37 w-378" />
        </div>
        <div className="flex gap-8">
          <SkeletonBar className="w-153" />
          <SkeletonBar className="w-153" />
          <SkeletonBar className="w-287" />
        </div>
        <div className="flex flex-col gap-8">
          <SkeletonBar tone="dark" className="h-23 w-871 max-w-full" />
          <SkeletonBar className="h-23 w-361" />
        </div>
        <div className="flex gap-8">
          <SkeletonBar className="w-153" />
          <SkeletonBar className="w-466 max-w-full" />
          <SkeletonBar className="w-171" />
        </div>
      </div>
    </div>

    <CompactLayoutSkeleton expanded className="lg:hidden" />
  </>
);

export const DirectoryLayout: FC<CardLayoutProps> = ({ content, favorited, onContact, onFavoriteToggle, loading }) => {
  if (!content || loading) return <DirectoryLayoutSkeleton />;

  const { name, verified, professions, location, years, photoUrl, profileHref, aboutExcerpt } = content;
  const { payments, specialties, languages, formats, clientTypes } = content;

  return (
    <>
      <div className="hidden items-stretch lg:flex">
        <div className="flex min-w-0 flex-1 items-start gap-24 px-32 py-24">
          <PractitionerAvatar name={name} photoUrl={photoUrl} className="h-167 w-162 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-16">
            <VerifiedName name={name} verified={verified} size="m" />
            <div className="flex flex-col gap-8">
              {professions.length > 0 && (
                <IconText icon={<BriefcaseIcon className="size-18" />} className="text-16">
                  <ProfessionList entries={professions} tagClassName="text-16" />
                </IconText>
              )}
              {location && (
                <IconText icon={<MapPinIcon className="size-18" />} className="text-16">
                  {location}
                </IconText>
              )}
              {payments.length > 0 && (
                <div className="flex min-w-0 items-center gap-8 text-16 text-neutral-black">
                  <ShieldCheckIcon className="size-18 shrink-0" />
                  <DotList items={payments} itemClassName="text-16" />
                </div>
              )}
            </div>
            <PractitionerTags languages={languages} formats={formats} />
            <SpecialtyPill
              specialties={specialties}
              pill
              size="m"
              icon={<SparklesIcon className="size-20 shrink-0 text-brand-dark-blue" />}
            />
            {aboutExcerpt && <p className="line-clamp-2 text-16 text-neutral-black/70">{aboutExcerpt}</p>}
          </div>
        </div>

        <div className="flex w-327 shrink-0 flex-col justify-between px-32 py-24">
          <FavoriteButton
            active={favorited}
            onClick={(event) => {
              event.stopPropagation();
              onFavoriteToggle?.();
            }}
            className="self-end size-40"
            iconClassName="size-32"
          />
          <ExperienceBlock yearsOfExperience={years} clientTypes={clientTypes} />
          <PractitionerActions profileHref={profileHref} onContact={onContact} className="w-263 flex-col gap-12" />
        </div>
      </div>

      <CompactLayout
        content={content}
        favorited={favorited}
        onContact={onContact}
        onFavoriteToggle={onFavoriteToggle}
        expanded
        showFavorite
        className="lg:hidden"
      />
    </>
  );
};
